import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { motion } from "framer-motion";
import { Minus, Plus, ArrowLeft, ArrowUpRight, Truck, Droplets, Flower2, Award, MessageCircle, Phone } from "lucide-react";
import { fetchProduct, fetchProducts, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Footer from "@/components/Footer";

const PHONE = "+40 (785) 454 967";
const PHONE_INTL = "40785454967";

const toneMap = { lavanda: "text-lavanda", gramur: "text-gramur", ocean: "text-ocean", neutru: "text-[#4A4844]" };
const catLabel = { detergent: "Detergent", degresant: "Degresant", balsam: "Balsam de rufe" };

const features = [
  { icon: Truck, label: "Transport gratuit" },
  { icon: Droplets, label: "Esență 100% originală" },
  { icon: Flower2, label: "Parfum puternic" },
  { icon: Award, label: "Calitate Premium" },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lenis = useLenis();
  const { addItem, orderNow } = useCart();
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    setQty(1);
    setProduct(null);
    fetchProduct(slug).then(setProduct).catch(() => navigate("/"));
    fetchProducts().then(setAll).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center text-[#4A4844] font-serif text-2xl">Se încarcă…</div>;
  }

  const related = all.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <main className="pt-28 md:pt-32">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <button
          data-testid="back-to-collection"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#4A4844] hover:text-[#0E0F12] transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Înapoi la colecție
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Imagine produs */}
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              data-testid="gallery-main"
              className="relative aspect-square overflow-hidden rounded-md border border-black/10 bg-white"
            >
              <img src={product.image} alt={product.name} className="w-full h-full object-contain p-4" />
            </motion.div>
          </div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            className="lg:pt-4"
            data-testid="product-detail"
          >
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#4A4844] mb-4">
              {catLabel[product.category] || "Produs"}
            </p>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0E0F12]">
              {product.name}
            </h1>
            <p className={`mt-3 text-sm tracking-widest uppercase ${toneMap[product.scent]}`}>
              {product.scent_label}
            </p>

            <div className="flex items-baseline gap-3 mt-7">
              <span data-testid="detail-price" className="font-serif text-3xl md:text-4xl text-[#0E0F12]">{fmtPrice(product.price)}</span>
              {product.old_price && (
                <span className="text-sm text-[#4A4844] line-through">{fmtPrice(product.old_price)}</span>
              )}
              <span className="ml-2 text-xs text-[#4A4844]">{product.volume}</span>
            </div>

            <p className="mt-7 text-[15px] leading-relaxed text-[#4A4844] max-w-xl">{product.description}</p>

            {product.notes?.length > 0 && (
              <div className="mt-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A4844] mb-3">Note olfactive</p>
                <div className="flex flex-wrap gap-2">
                  {product.notes.map((n) => (
                    <span key={n} className="text-xs tracking-wide border border-black/12 rounded-full px-3 py-1.5 text-[#0E0F12]">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity + actions */}
            <div className="mt-9 space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-black/15 rounded-sm">
                  <button
                    data-testid="detail-qty-decrease"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-11 h-12 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12] transition-colors"
                  >
                    <Minus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <span data-testid="detail-qty-value" className="w-10 text-center text-sm">{qty}</span>
                  <button
                    data-testid="detail-qty-increase"
                    onClick={() => setQty((q) => q + 1)}
                    className="w-11 h-12 flex items-center justify-center text-[#4A4844] hover:text-[#0E0F12] transition-colors"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  data-testid="detail-order-now"
                  onClick={() => orderNow(product, qty)}
                  className="flex-1 py-4 bg-[#1E7A3D] text-white text-sm tracking-widest uppercase rounded-sm hover:bg-[#176230] hover:-translate-y-[2px] transition-all duration-300"
                >
                  Comandă acum
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  data-testid="detail-whatsapp"
                  href={`https://wa.me/${PHONE_INTL}?text=${encodeURIComponent(`Bună! Vreau să comand: ${product.name} (${fmtPrice(product.price)}) × ${qty}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 py-4 bg-[#25D366] text-white text-sm tracking-wide uppercase rounded-sm hover:-translate-y-[2px] transition-transform duration-300"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.8} /> WhatsApp
                </a>
                <a
                  data-testid="detail-call"
                  href={`tel:+${PHONE_INTL}`}
                  className="inline-flex items-center justify-center gap-2 py-4 border border-black/20 text-[#0E0F12] text-sm tracking-wide uppercase rounded-sm hover:bg-[#0E0F12] hover:text-[#EDEAE3] transition-colors duration-300"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.8} /> Sună-ne
                </a>
              </div>

              <p className="text-xs text-[#4A4844] text-center">Sună-ne la <a href={`tel:+${PHONE_INTL}`} className="text-[#0E0F12] font-medium">{PHONE}</a></p>

              <button
                data-testid="detail-add-to-cart"
                onClick={() => addItem(product, qty)}
                className="w-full text-xs tracking-widest uppercase text-[#4A4844] hover:text-[#0E0F12] underline underline-offset-4 transition-colors"
              >
                Adaugă doar în coș
              </button>
            </div>

            {/* Feature chips */}
            <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
              {features.map((f) => (
                <span key={f.label} className="inline-flex items-center gap-2.5 text-[13px] text-[#0E0F12]">
                  <f.icon className="w-4 h-4 text-gramur" strokeWidth={1.6} />
                  {f.label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-28 md:mt-36 border-t border-black/10 pt-16">
            <h2 className="font-serif font-light text-3xl md:text-4xl tracking-tight mb-10">
              Poate îți place și <span className="italic text-[#4A4844]">·</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((p) => (
                <article
                  key={p.id}
                  data-testid={`related-card-${p.slug}`}
                  onClick={() => navigate(`/produs/${p.slug}`)}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-sm border border-black/10 bg-white">
                    <img src={p.image} alt={p.name} className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-[1200ms]" />
                    <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-[#0E0F12] text-[#EDEAE3] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                    </span>
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-2">
                    <h3 className="font-serif text-xl leading-tight">{p.name}</h3>
                    <p className="text-sm whitespace-nowrap">{fmtPrice(p.price)}</p>
                  </div>
                  <p className={`text-[10px] tracking-[0.3em] uppercase mt-1 ${toneMap[p.scent]}`}>{p.scent_label}</p>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-28">
        <Footer />
      </div>
    </main>
  );
}
